import { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { TOOLTIP_CONTENT_CLASSNAME, TOOLTIP_ARROW_CLASSNAME } from './tooltip';
import { cn } from '@/lib/utils';

/**
 * Normalizes descriptions that often arrive as plain strings from APIs:
 * - `<br>`, `<br/>`, `<br />` → newline (tags are not rendered as HTML otherwise)
 * - Literal two-character sequences `\\r\\n`, `\\n`, `\\t` (common when JSON/Python
 *   stringifies escapes) → real whitespace
 * Actual newline characters in `text` are left as-is.
 */
function preparePlainDescriptionText(raw: string): string {
  let s = raw.replace(/<br\s*\/?>/gi, '\n');
  s = s.replace(/\\r\\n/g, '\n');
  s = s.replace(/\\n/g, '\n');
  s = s.replace(/\\t/g, '\t');
  return s;
}

interface TruncateTextWithTooltipProps {
  /** Plain string shown in the truncated area (used for overflow measurement). */
  text: string;
  className?: string;
  interpretPlainTextFormat?: boolean;
  /**
   * Maximum number of lines to display before truncating.
   * - undefined or 1: Single-line truncation (default)
   * - 2 or more: Multi-line truncation with line-clamp
   */
  maxLines?: number;
  tooltipProps?: React.ComponentProps<typeof TooltipPrimitive.Content>;
  tooltipDelay?: number;
}

export const TruncateTextWithTooltip: React.FC<
  TruncateTextWithTooltipProps
> = ({
  text,
  interpretPlainTextFormat = false,
  className,
  maxLines = 1,
  tooltipProps,
  tooltipDelay = 0,
}) => {
  const ref = useRef<HTMLParagraphElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const displayText = useMemo(
    () => (interpretPlainTextFormat ? preparePlainDescriptionText(text) : text),
    [text, interpretPlainTextFormat],
  );

  const checkOverflow = useCallback(() => {
    if (ref.current) {
      // Check for both horizontal and vertical overflow
      const horizontalOverflow =
        ref.current.scrollWidth > ref.current.clientWidth;
      const verticalOverflow =
        ref.current.scrollHeight > ref.current.clientHeight;
      setIsOverflowing(horizontalOverflow || verticalOverflow);
    }
  }, []);

  const debouncedCheckOverflow = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      checkOverflow();
    }, 150);
  }, [checkOverflow]);

  useEffect(() => {
    checkOverflow();
    window.addEventListener('resize', debouncedCheckOverflow);

    return () => {
      window.removeEventListener('resize', debouncedCheckOverflow);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [displayText, checkOverflow, debouncedCheckOverflow]);

  // Determine truncation classes based on maxLines
  // Note: line-clamp classes are safelisted in styles.css via @source inline()
  const isMultiLine = maxLines > 1;
  const truncationClasses = isMultiLine
    ? interpretPlainTextFormat
      ? `whitespace-pre-line line-clamp-${maxLines}`
      : `whitespace-normal line-clamp-${maxLines}`
    : 'truncate';

  const content = (
    <span ref={ref} className={cn('min-w-0', truncationClasses, className)}>
      {displayText}
    </span>
  );

  if (!isOverflowing) return content;

  const { className: tooltipClassName, ...tooltipContentProps } =
    tooltipProps || {};

  return (
    <TooltipPrimitive.Root delayDuration={tooltipDelay}>
      <TooltipPrimitive.Trigger asChild>{content}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          className={cn(TOOLTIP_CONTENT_CLASSNAME, tooltipClassName)}
          {...tooltipContentProps}
        >
          <span className='wrap-break-word max-w-full whitespace-pre-line text-left'>
            {displayText}
          </span>
          <TooltipPrimitive.Arrow className={TOOLTIP_ARROW_CLASSNAME} />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
};
